import { useState } from 'react';

interface Aircraft {
  id: string;
  name: string;
  tagline: string;
  image: string;
  specs: {
    passengers: string;
    range: string;
    speed: string;
    altitude: string;
  };
  features: string[];
}

const aircraftData: Aircraft[] = [
  {
    id: 'djt-city',
    name: 'DJT City',
    tagline: 'Urban Air Mobility Redefined',
    image: 'https://readdy.ai/api/search-image?query=Sleek%20futuristic%20electric%20vertical%20takeoff%20and%20landing%20aircraft%20eVTOL%20drone%20aircraft%20with%20multiple%20rotors%20hovering%20above%20modern%20city%20skyline%2C%20white%20and%20cyan%20color%20scheme%2C%20clean%20minimalist%20design%2C%20professional%20product%20photography%2C%20simple%20gradient%20background%2C%20high-tech%20aviation%20innovation%2C%20sustainable%20urban%20air%20mobility%20vehicle%2C%20aerodynamic%20streamlined%20body%2C%20advanced%20propulsion%20system&width=800&height=600&seq=djt-city-select&orientation=landscape',
    specs: {
      passengers: '2 passengers',
      range: '35 km',
      speed: '110 km/h',
      altitude: '500 m'
    },
    features: [
      'Perfect for city commutes',
      'Zero emissions electric propulsion',
      'Whisper-quiet operation',
      'Advanced safety systems',
      'Autonomous flight capability'
    ]
  },
  {
    id: 'djt-region',
    name: 'DJT Region',
    tagline: 'Extended Range for Regional Travel',
    image: 'https://readdy.ai/api/search-image?query=Advanced%20futuristic%20electric%20vertical%20takeoff%20and%20landing%20aircraft%20eVTOL%20drone%20aircraft%20with%20extended%20range%20capabilities%2C%20larger%20passenger%20capacity%2C%20multiple%20rotors%2C%20flying%20over%20suburban%20landscape%2C%20white%20and%20purple%20color%20scheme%2C%20clean%20minimalist%20design%2C%20professional%20product%20photography%2C%20simple%20gradient%20background%2C%20high-tech%20aviation%20innovation%2C%20sustainable%20regional%20air%20mobility%20vehicle%2C%20aerodynamic%20streamlined%20body&width=800&height=600&seq=djt-region-select&orientation=landscape',
    specs: {
      passengers: '1+5 passengers',
      range: '250 km',
      speed: '150 km/h',
      altitude: '1000 m'
    },
    features: [
      'Ideal for regional connections',
      'Extended battery range',
      'Spacious cabin design',
      'All-weather capability',
      'Premium comfort features'
    ]
  }
];

export function SelectAircraft() {
  const [selectedAircraft, setSelectedAircraft] = useState<string>(aircraftData[1].id);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const currentAircraft = aircraftData.find(a => a.id === selectedAircraft) || aircraftData[1];

  const handleBookNow = () => {
    setShowBookingModal(true);
  };

  return (
    <section id="aircraft" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="text-5xl lg:text-6xl font-light text-gray-900 mb-4">
            Select Yours
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose the perfect air aircraft for your journey. From city hops to regional travel.
          </p>
        </div>

        {/* Aircraft Selector Tabs */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-gray-100 rounded-full p-2">
            {aircraftData.map((aircraft) => (
              <button
                key={aircraft.id}
                onClick={() => setSelectedAircraft(aircraft.id)}
                className={`px-8 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedAircraft === aircraft.id
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {aircraft.name}
              </button>
            ))}
          </div>
        </div>

        {/* Aircraft Display */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="order-2 lg:order-1">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500/20 to-purple-500/20 rounded-3xl blur-3xl"></div>
              <img
                src={currentAircraft.image}
                alt={currentAircraft.name}
                className="relative w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Details */}
          <div className="order-1 lg:order-2">
            <h3 className="text-4xl font-light text-gray-900 mb-2">
              {currentAircraft.name}
            </h3>
            <p className="text-xl text-teal-500 mb-8">
              {currentAircraft.tagline}
            </p>

            {/* Specifications */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-user-line text-2xl text-teal-500"></i>
                </div>
                <div className="text-sm text-gray-500 mb-1">Capacity</div>
                <div className="text-lg font-medium text-gray-900">{currentAircraft.specs.passengers}</div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-map-pin-range-line text-2xl text-purple-500"></i>
                </div>
                <div className="text-sm text-gray-500 mb-1">Range</div>
                <div className="text-lg font-medium text-gray-900">{currentAircraft.specs.range}</div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-speed-line text-2xl text-cyan-500"></i>
                </div>
                <div className="text-sm text-gray-500 mb-1">Max Speed</div>
                <div className="text-lg font-medium text-gray-900">{currentAircraft.specs.speed}</div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="w-12 h-12 bg-indigo-50 rounded-lg flex items-center justify-center mb-3">
                  <i className="ri-flight-takeoff-line text-2xl text-indigo-500"></i>
                </div>
                <div className="text-sm text-gray-500 mb-1">Altitude</div>
                <div className="text-lg font-medium text-gray-900">{currentAircraft.specs.altitude}</div>
              </div>
            </div>

            {/* Features */}
            <div className="mb-8">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Key Features</h4>
              <ul className="space-y-3">
                {currentAircraft.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-0.5"></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleBookNow}
              className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-4 rounded-full text-lg font-medium hover:shadow-xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Book {currentAircraft.name} Now
            </button>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowBookingModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-3xl font-light text-gray-900">Book Your Flight</h3>
                <button onClick={() => setShowBookingModal(false)} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>

              <form className="space-y-6" data-readdy-form id="aircraft-booking-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Aircraft</label>
                    <input
                      type="text"
                      name="aircraft"
                      value={currentAircraft.name}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Location</label>
                    <input
                      type="text"
                      name="pickupLocation"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Downtown Helipad"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Destination</label>
                    <input
                      type="text"
                      name="destination"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Airport Terminal"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Flight Date</label>
                    <input
                      type="date"
                      name="flightDate"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Flight Time</label>
                    <input
                      type="time"
                      name="flightTime"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Passengers</label>
                  <input
                    type="number"
                    name="passengers"
                    min="1"
                    max={currentAircraft.specs.passengers.split(' ')[0]}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Requirements (Optional)</label>
                  <textarea
                    name="specialRequirements"
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    placeholder="Any special requests or requirements..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-4 rounded-full text-lg font-medium hover:shadow-xl transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Confirm Booking
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
